import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Music, Target, TrendingUp } from "lucide-react";
import { toast } from "sonner";

interface SingingPreferencesFormProps {
  userId: string;
  onComplete?: () => void;
  existingPreferences?: {
    singing_genres?: string[];
    singing_level?: string;
    singing_goals?: string;
  };
}

const GENRES = [
  "Pop", "Rock", "Jazz", "Classical", "R&B", "Country", 
  "Musical Theatre", "Opera", "Gospel", "Blues", "Folk", "Metal"
];

const LEVELS = [
  { value: "beginner", label: "Beginner", description: "Just starting out" },
  { value: "intermediate", label: "Intermediate", description: "Some experience" },
  { value: "advanced", label: "Advanced", description: "Extensive experience" },
  { value: "professional", label: "Professional", description: "Performing regularly" }
];

export const SingingPreferencesForm = ({ userId, onComplete, existingPreferences }: SingingPreferencesFormProps) => {
  const [selectedGenres, setSelectedGenres] = useState<string[]>(existingPreferences?.singing_genres || []);
  const [level, setLevel] = useState<string>(existingPreferences?.singing_level || "");
  const [goals, setGoals] = useState<string>(existingPreferences?.singing_goals || "");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleGenre = (genre: string) => {
    setSelectedGenres(prev =>
      prev.includes(genre)
        ? prev.filter(g => g !== genre)
        : [...prev, genre]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (selectedGenres.length === 0) {
      toast.error("Please select at least one genre");
      return;
    }
    
    if (!level) {
      toast.error("Please select your singing level");
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          singing_genres: selectedGenres,
          singing_level: level,
          singing_goals: goals || null,
          preferences_completed: true
        })
        .eq("id", userId);

      if (error) throw error;

      toast.success("Preferences saved successfully!");
      onComplete?.();
    } catch (error) {
      console.error("Error saving preferences:", error);
      toast.error("Failed to save preferences");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Genres Selection */}
      <Card className="border-2 border-primary/20">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
              <Music className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle>Musical Genres</CardTitle>
              <CardDescription>What styles do you want to explore?</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {GENRES.map((genre) => (
              <Badge
                key={genre}
                variant={selectedGenres.includes(genre) ? "default" : "outline"}
                className="cursor-pointer transition-all hover:scale-105 text-sm py-2 px-4"
                onClick={() => toggleGenre(genre)}
              >
                {genre}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Singing Level */}
      <Card className="border-2 border-primary/20">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle>Experience Level</CardTitle>
              <CardDescription>Where are you in your vocal journey?</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 gap-3">
            {LEVELS.map((lvl) => (
              <button
                key={lvl.value}
                type="button"
                onClick={() => setLevel(lvl.value)}
                className={`p-4 rounded-lg border-2 text-left transition-all hover:scale-105 ${
                  level === lvl.value
                    ? "border-primary bg-primary/10 shadow-lg"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <div className="font-semibold text-base mb-1">{lvl.label}</div>
                <div className="text-sm text-muted-foreground">{lvl.description}</div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Goals (Optional) */}
      <Card className="border-2 border-primary/20">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle>Your Goals <span className="text-sm text-muted-foreground">(Optional)</span></CardTitle>
              <CardDescription>What do you hope to achieve?</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Textarea
            value={goals}
            onChange={(e) => setGoals(e.target.value)}
            placeholder="e.g., Improve vocal range, prepare for auditions, gain confidence..."
            className="min-h-[100px] resize-none"
            maxLength={500}
          />
          <p className="text-xs text-muted-foreground mt-2">
            {goals.length}/500 characters
          </p>
        </CardContent>
      </Card>

      <Button
        type="submit"
        size="lg"
        className="w-full"
        disabled={isSubmitting}
      >
        {isSubmitting ? "Saving..." : "Save Preferences"}
      </Button>
    </form>
  );
};
